'use client';

import { NotFoundScreen } from '@modules/shared/components/status-screens/not-found-screen/not-found-screen';
import { useWorkspaceHomePath } from 'app/_utils/workspace-home-path';

import * as Sentry from '@sentry/nextjs';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React, { useEffect } from 'react';
import styled from 'styled-components';

const ErrorMessage = styled.p`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  text-align: center;
`;

const AlignCenter = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 2rem;
`;

const LogoutButton = styled.button`
  background-color: #ff4d4f;
  border: none;
  color: white;
  padding: 0.5rem 1rem;
  font-size: 1rem;
  cursor: pointer;
  border-radius: 4px;
`;

interface ErrorProps {
  error: Error & {
    digest?: string;
    details?: { logoutRequired?: boolean; recipient?: string; [key: string]: unknown };
  };
  reset: () => void;
}

const DefaultNotFound: React.FC = () => {
  const workspaceHomePath = useWorkspaceHomePath();
  const homePath = workspaceHomePath || '/';

  return (
    <NotFoundScreen>
      Let&apos;s goat you back <Link href={homePath}>Home</Link>.
    </NotFoundScreen>
  );
};

const ErrorContent = ({ error }: ErrorProps) => {
  const router = useRouter();

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    router.push('/login');
  };

  if (error.details?.logoutRequired) {
    return (
      <AlignCenter data-testid='error-align-center-logout'>
        <ErrorMessage>{error.message}</ErrorMessage>
        <LogoutButton onClick={handleLogout}>Log Out</LogoutButton>
      </AlignCenter>
    );
  }

  return (
    <AlignCenter data-testid='error-align-center-not-found'>
      <DefaultNotFound />
    </AlignCenter>
  );
};

const Error: React.FC<ErrorProps> = (props) => (
  <Sentry.ErrorBoundary fallback={<DefaultNotFound />}>
    <ErrorContent {...props} />
  </Sentry.ErrorBoundary>
);

export default Error;
